import { Box, Button, Dialog, Drawer, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'

import PracticeModeViewRenderer from './practice-mode/PracticeModeViewRenderer'
import LearnModeViewRenderer from './learn-mode/LearnModeViewRenderer'
import CustomTwoValueSwitch from '../../../shared/components/common/CustomTwoValueSwitch'
import { useSimbaAIStore } from '../../store/simbaai.store'
import simbaLogo from '../../../shared/assets/Habba-Logo.png'
import { useSearchParams } from 'react-router-dom'
import { Icons } from '../../../shared/utils/icons.jsx'
import SimbaDetailsDailog from '../common/docs-and-support/SimbaDetailsDialog'

const SimbaAIForStudents = observer( () => {

    // const [showScrollToBottomButton, setShowScrollToBottomButton] = useState( false )
    const [restrictFullScreenDialogState, setRestrictFullScreenDialogState] = useState( false )
    const [showSimbaAiDetails, setShowSimbaAiDetails] = useState( false )
    const [loading, setLoading] = useState( true )


    const { palette, layout, shadows } = useTheme()

    const [searchParams, setSearchParams] = useSearchParams( { simbaAIOpen: 'false', practiceQuiz: "", simbaAIMode: "learn" } )
    const simbaAIOpen = searchParams.get( 'simbaAIOpen' ) === 'true'
    const simbaAIMode = searchParams.get( 'simbaAIMode' ) || "learn"

    const SimbaAIStore = useSimbaAIStore()

    const AiMentorArea = useRef( null )


    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const enterFullScreen = () => {
        const quizContainer = document.getElementById( "simba-quiz-container" )
        if ( !window.document.fullscreenElement ) {
            if ( quizContainer ) {
                if ( quizContainer.requestFullscreen ) {
                    quizContainer.requestFullscreen()
                } else if ( quizContainer.webkitRequestFullscreen ) {
                    // Safari
                    quizContainer.webkitRequestFullscreen()
                } else if ( quizContainer.msRequestFullscreen ) {
                    // IE/Edge
                    quizContainer.msRequestFullscreen()
                }
            }
        } else {
            window.document.exitFullscreen()
        }
        setRestrictFullScreenDialogState( false )
    }

    const setSimbaAIMode = value => {
        setSearchParamsValues( { simbaAIMode: value } )
        SimbaAIStore.setView( "home", value === "learn" ? "right" : "left", false )
    }

    const closeAiMentorDialog = () => {
        if ( ['simba-ai-response', 'completed-typing-simba-ai-response'].includes( SimbaAIStore.getView.current ) ) {
            if ( SimbaAIStore.getAiResponsesQueue.request ) {
                SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
                SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
            }
        }
        SimbaAIStore.setView( 'home' )
        setSearchParamsValues( { simbaAIOpen: "", practiceQuiz: "", simbaAIMode: "", selectedSubject: "" } )
    }


    const getPracticeSessions = useCallback( async () => {
        setLoading( true )
        await SimbaAIStore.fetchPracticeSessions()
        const uniqueSubCodes = {}
        const uniqueSubjects = []
        for ( const ps of SimbaAIStore.getPracticeSessions ) {
            if ( !uniqueSubCodes[ps.subject.subject_code] ) {
                uniqueSubjects.push( ps.subject )
                uniqueSubCodes[ps.subject.subject_code] = true
            }
        }
        SimbaAIStore.setTopicsAvailableSubjects( uniqueSubjects )
        setLoading( false )
    }, [SimbaAIStore] )

    useEffect( () => {
        getPracticeSessions()
    }, [getPracticeSessions] )

    useEffect( () => {

        const handleFullScreenChange = event => {
            console.log( SimbaAIStore.getView.current, !document.fullscreenElement && SimbaAIStore.getView.current === "question" )

            if ( !document.fullscreenElement && SimbaAIStore.getView.current === "question" ) {
                setRestrictFullScreenDialogState( true )
            }
        }

        window.document.addEventListener( 'fullscreenchange', handleFullScreenChange )

        return () => window.document.removeEventListener( 'fullscreenchange', handleFullScreenChange )
    }, [SimbaAIStore] )


    return (
        <Box>
            <SimbaDetailsDailog open={showSimbaAiDetails} onClose={() => setShowSimbaAiDetails( false )} />

            <Drawer
                anchor='right'
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: "blur(5px)"
                        }
                    },
                    paper: {
                        className: "slim-custom-scrollbar",
                        ref: AiMentorArea,
                        sx: {
                            width: { sm: "95vw", xs: "100vw" },
                            height: "100dvh",
                            maxWidth: "1200px",
                            display: "flex",
                            flexDirection: "column",
                            zIndex: 2000,
                            borderRadius: { md: "20px 0 0 20px", xs: "0" }
                        }
                    }
                }}
                onClose={closeAiMentorDialog}
                open={simbaAIOpen}
            >

                {restrictFullScreenDialogState && <Dialog slotProps={{ backdrop: { sx: { backdropFilter: "blur(5px)" } }, paper: { sx: { maxWidth: "350px" } } }} open={restrictFullScreenDialogState}>
                    <Box>
                        <Typography
                            color={palette.error.contrastText}
                            sx={{
                                padding: "10px",
                                bgcolor: palette.error.main
                            }}>
                            Warning
                        </Typography>
                        <Box sx={{
                            padding: layout.pagePadding
                        }}>
                            <Typography variant='subtitle2' component="p">Please stay in the full screen mode while practicing.</Typography>
                        </Box>
                        <Box
                            sx={{
                                borderTop: `4px dashed ${palette.borderColor}`,
                                padding: "10px 20px",
                                bgcolor: palette.contentBg,
                                display: "flex",
                                justifyContent: "flex-end"
                            }}>
                            <Button onClick={enterFullScreen} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} variant='contained' disableElevation color='primary' >Okay</Button></Box>
                    </Box>

                </Dialog>}

                <Box
                    sx={{
                        position: "sticky",
                        top: "0",
                        bgcolor: palette.layoutBackground,
                        padding: '10px',
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "20px",
                        alignItems: "center",
                        zIndex: 2000 * 2,
                        backdropFilter: "blur(3px)"
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center"
                        }}>
                        <Box
                            sx={{
                                borderRadius: "10px",
                                border: `2px solid ${palette.common.white}`,
                                width: "30px",
                                aspectRatio: "1 / 1",
                                width: "35px",
                                height: "35px"
                            }}>
                            <Box
                                component="img"
                                src={simbaLogo}
                                sx={{
                                    height: "100%",
                                    width: "100%",
                                    objectFit: "cover"
                                }} />
                        </Box>
                        <Box>
                            <Typography
                                color={palette.common.white}
                                component="p"
                                sx={{
                                    fontWeight: "700",
                                    fontSize: "16px"
                                }}> Simba AI</Typography>
                            <Typography
                                color={palette.common.white}
                                component="p"
                                sx={{
                                    display: { sm: "block", xs: "none" },
                                    fontSize: "12px",
                                    marginTop: "-5px"
                                }}> Practice your topics with the help of Simba AI </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center"
                        }}>


                        <Box
                            sx={{
                                display: "flex",
                                border: `1px solid ${palette.common.white}`,
                                boxShadow: shadows[2],
                                padding: "5px 10px",
                                borderRadius: "20px",
                                gap: "10px",
                                alignItems: "center",
                                background: `linear-gradient(125.39deg, #139CFF77 -32.54%, #C5409777 110.4%)`
                            }}>
                            <Typography
                                onClick={event => setSimbaAIMode( "learn" )}
                                color={palette.common.white}
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    cursor: "pointer"
                                }}>Learn</Typography>
                            <CustomTwoValueSwitch color="primary" checked={simbaAIMode === 'practice'} onChange={event => setSimbaAIMode( event.target.checked ? "practice" : "learn" )} size="small" />
                            <Typography
                                onClick={event => setSimbaAIMode( "practice" )}
                                color={palette.common.white}
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    cursor: "pointer"
                                }}>Practice</Typography>
                        </Box>
                        <Tooltip title="Learn about Practice Sessions with Simba AI">
                            <Button startIcon={Icons.default.Info} sx={{ color: palette.common.white, display: { xs: "none", sm: "flex" } }} size='small' onClick={() => setShowSimbaAiDetails( true )} >
                                Info
                            </Button>
                        </Tooltip>
                        <IconButton sx={{ color: palette.common.white }} onClick={closeAiMentorDialog}>
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Box>
                </Box>

                {!loading && simbaAIMode === 'learn' && <LearnModeViewRenderer closeAiMentorDialog={closeAiMentorDialog} />}

                {!loading && simbaAIMode === 'practice' && <Box
                    component="section"
                    id="simba-quiz-container"
                    sx={{
                        bgcolor: palette.background.paper,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1
                    }}>
                    <PracticeModeViewRenderer />
                </Box>}

            </Drawer>
        </Box>
    );
} )

export default SimbaAIForStudents